/**
 * Check if a chapter has any partidas in its tree (direct or nested).
 *
 * @param {Object} chapter - Chapter object that may contain `partidas` and `chapters`.
 * @returns {boolean} True if the chapter or any of its subchapters has partidas.
 */
const hasPartidasInTree = chapter => {
  if (Array.isArray(chapter.partidas) && chapter.partidas.length > 0) {
    return true
  }
  if (Array.isArray(chapter.chapters)) {
    return chapter.chapters.some(sub => hasPartidasInTree(sub))
  }
  return false
}

/**
 * Recursively annotate a chapter tree by attaching the provided projectId
 * to every `partida` object found in the tree. Also filters out chapters
 * that have no partidas (directly or in nested subchapters).
 *
 * If `chapters` is not an array the value is returned as-is.
 *
 * @param {Array<Object>|any} chapters - Array of chapter objects (may contain nested `chapters` and `partidas`).
 * @param {string|number} projectId - Project identifier to attach to each `partida` as `projectId`.
 * @returns {Array<Object>|any} A new chapters array where each `partida` has `projectId` set and empty chapters are removed.
 */
export const annotateChapters = (chapters, projectId) => {
  if (!Array.isArray(chapters)) return chapters
  return chapters
    .map(ch => ({
      ...ch,
      chapters: annotateChapters(ch.chapters, projectId),
      partidas: Array.isArray(ch.partidas)
        ? ch.partidas.map(p => ({ ...p, projectId }))
        : ch.partidas,
    }))
    .filter(ch => hasPartidasInTree(ch))
}

/**
 * Collects identifiers for a chapter: a chapter marker ("<chapterId>_c"),
 * all nested `partida` ids, and any decomposition ids for each partida
 * in the form "<partidaId>_<index>". The traversal is recursive so
 * nested subchapters are processed as well.
 *
 * @param {Object} chapter - Chapter object containing `chapterId`, optional `chapters` and `partidas` arrays.
 * @returns {string[]} Array of ids including the chapter marker, partida ids and decomposition ids.
 */
export const getAllChapterPartidaAndDecompIds = chapter => {
  let ids = [`${chapter.chapterId}_c`]
  if (Array.isArray(chapter.chapters)) {
    chapter.chapters.forEach(sub => {
      ids = ids.concat(getAllChapterPartidaAndDecompIds(sub))
    })
  }
  if (Array.isArray(chapter.partidas)) {
    chapter.partidas.forEach(partida => {
      ids.push(partida.partidaId)
      if (Array.isArray(partida.decomposition)) {
        partida.decomposition.forEach((_, idx) => {
          ids.push(`${partida.partidaId}_${idx}`)
        })
      }
    })
  }
  return ids
}

/**
 * Returns a flat list of `chapterId` values for all subchapters of the
 * provided chapter (recursive). This does not include any partida or
 * decomposition ids, only the plain subchapter ids.
 *
 * @param {Object} chapter - Chapter object that may contain nested `chapters`.
 * @returns {string[]} Flat array of subchapter ids.
 */
export const getAllSubchapterPlainIds = chapter => {
  let ids = []
  if (Array.isArray(chapter.chapters)) {
    chapter.chapters.forEach(sub => {
      ids.push(sub.chapterId)
      ids = ids.concat(getAllSubchapterPlainIds(sub))
    })
  }
  return ids
}

/**
 * Build an array containing the partida id followed by its decomposition
 * ids (if any) using the pattern "<partidaId>_<index>" for decompositions.
 *
 * @param {Object} partida - Partida object with `partidaId` and optional `decomposition` array.
 * @returns {string[]} Array where the first element is the partidaId and subsequent
 * entries are decomposition ids.
 */
export const getPartidaAndDecompIds = partida => {
  let ids = [partida.partidaId]
  if (Array.isArray(partida.decomposition)) {
    partida.decomposition.forEach((_, idx) => {
      ids.push(`${partida.partidaId}_${idx}`)
    })
  }
  return ids
}

/**
 * Collect all `partidaId` values present in a chapter and its nested
 * subchapters (recursive). Returns only the plain partida ids (no decomposition ids).
 *
 * @param {Object} chapter - Chapter object that may contain `partidas` and `chapters`.
 * @returns {string[]} Flat array of partida ids found in the chapter tree.
 */
export const collectPartidaIdsFromChapter = chapter => {
  let ids = []
  if (Array.isArray(chapter.partidas)) {
    ids = ids.concat(chapter.partidas.map(p => p.partidaId))
  }
  if (Array.isArray(chapter.chapters)) {
    chapter.chapters.forEach(sub => {
      ids = ids.concat(collectPartidaIdsFromChapter(sub))
    })
  }
  return ids
}

/**
 * Round a numeric value to 2 decimal places and return a Number.
 * This helps avoid floating point artifacts like 1173.6999999999998
 */
export const roundCurrency = value => {
  const num = Number(value) || 0
  // toFixed returns a string; convert back to Number to preserve numeric type
  return Number(num.toFixed(2))
}

/**
 * Convert a number (assumed in main currency units) to integer cents.
 */
export const toCents = value => {
  if (value === null || value === undefined || value === "") return 0
  return Math.round(Number(value) * 100)
}

/**
 * Convert integer cents to number with 2 decimals.
 */
export const fromCents = cents => {
  return Number((Number(cents) / 100).toFixed(2))
}

/**
 * Sum an array of monetary numbers safely via cents integer arithmetic.
 */
export const safeSum = (arr = []) => {
  const totalCents = arr.reduce((acc, v) => acc + toCents(v), 0)
  return fromCents(totalCents)
}

/**
 * Add two monetary numbers safely.
 */
export const safeAdd = (a, b) => {
  return fromCents(toCents(a) + toCents(b))
}

/**
 * Check if a monetary number is effectively zero within epsilon.
 */
export const isNearZero = (v, epsilon = 0.000001) =>
  Math.abs(Number(v) || 0) < epsilon

/**
 * Force rounding to 2 decimals using cents arithmetic (alternative to roundCurrency).
 */
export const strictRound2 = v => fromCents(toCents(v))
